import { useCallback, useMemo, useState } from "react";
import type { ISelectablePickerItem, ISelectionPickerController, IUseSelectionPickerParams } from "./selection-picker.types";

/**
 * Controller for "select up to N items, then order them". Owns its selection
 * state — read `selected` and seed via `reset`.
 */
export function useSelectionPicker<TItem extends ISelectablePickerItem>({
    defaultSelected = [],
    maxSelectable,
}: IUseSelectionPickerParams<TItem>): ISelectionPickerController<TItem> {
    const [selected, setSelected] = useState<TItem[]>(defaultSelected);

    const isSelected = useCallback((item: TItem) => selected.some(current => current.id === item.id), [selected]);

    const toggle = useCallback(
        (item: TItem) => {
            setSelected(prev => {
                const exists = prev.some(current => current.id === item.id);
                if (exists) {
                    return prev.filter(current => current.id !== item.id);
                }
                if (prev.length >= maxSelectable) {
                    return prev;
                }
                return [...prev, item];
            });
        },
        [maxSelectable]
    );

    const reorder = useCallback((items: TItem[]) => setSelected(items), []);
    const clear = useCallback(() => setSelected([]), []);
    const reset = useCallback((items: TItem[]) => setSelected(items), []);

    return useMemo(
        () => ({
            selected,
            maxSelectable,
            isFull: selected.length >= maxSelectable,
            isEmpty: selected.length === 0,
            isSelected,
            toggle,
            reorder,
            clear,
            reset,
        }),
        [selected, maxSelectable, isSelected, toggle, reorder, clear, reset]
    );
}
